import React, { FC, useCallback, useEffect, useReducer, useState } from 'react';

import { Resources } from '@ariksa/inventory-core';
import { NativeResources } from '@ariksa/inventory-core/api';
import { Box, Center, Stack, useDisclosure } from '@chakra-ui/react';
import { isEmpty, isEqual } from 'lodash';
import round from 'lodash/round';
import { useDispatch, useSelector } from 'react-redux';
import { customTheme } from 'theme';

import { useFlags } from 'app/flagsmith';
import { featureFlags, isFeatureEnabled } from 'app/flagsmith/features';
import {
  CustomTooltip,
  renderSeverityBar,
  StackedCell,
  WithResourceIcon,
} from 'components/DataDisplay';
import { BorderedBox } from 'components/DataDisplay/Box/BorderedBox';
import { CustomTable2 as Table } from 'components/DataDisplay/NewTable/Table';
import { Sorted } from 'components/DataDisplay/NewTable/types';
import {
  ActionButton,
  getPublishedTime,
  publishedAtOptions,
} from 'components/DataEntry';
import { IconTypes, ListIcon } from 'components/Icons';
import { PageInfo } from 'components/Navigation';
import { CVEDetails } from 'containers/ActiveCloudResource/Components/ResourceMetadataDrawer/ResourceMetadata/Vulnerability/CVE/CVEDetails';
import { renderCVSSScoreTag } from 'containers/ActiveCloudResource/Components/ResourceMetadataDrawer/ResourceMetadata/Vulnerability/CVE/utils';
import { ResourceVulnerabilitiesFilters } from 'containers/Visibility/Vulnerabilities/Components/ResourceVulnerabilities/ResourceVulnerabilitiesFilters';
import {
  exploitableOptions,
  patchAvailableOptions,
  vulnerabilitySeverityOptions,
} from 'containers/Visibility/Vulnerabilities/Components/utils';
import { HostsDrawer } from 'containers/Visibility/Vulnerabilities/Components/VulnerabilityTable/HostsDrawer';
import { selectVulnerabilities } from 'containers/Visibility/Vulnerabilities/selectors';
import { actions } from 'containers/Visibility/Vulnerabilities/slice';
import { VulnerabilityType } from 'containers/Visibility/Vulnerabilities/types';

interface Props {
  isContainer: boolean;
}

export const ResourceVulnerabilityDetailsTable: FC<Props> = props => {
  const { isContainer } = props;
  const { resourceVulnerabilities, resourceInsight } = useSelector(
    selectVulnerabilities,
  );
  const { data: insight } = resourceInsight;
  const dispatch = useDispatch();
  const { new_vuln_dashboard: vulnerabilityV3Flag } = useFlags([
    featureFlags.vulnerabilityV3,
  ]);
  const useScanVaultVulnerabilityFindings = isFeatureEnabled(
    vulnerabilityV3Flag,
  );
  const cveDetailsDisclosure = useDisclosure();
  const [sortInfo, setSortInfo] = useState<Sorted>({
    sortOrder: 'desc',
    sortField: 'severity',
  });
  const hostsDisclosure = useDisclosure();
  const [state, updateState] = useReducer(
    (prev, next) => {
      return { ...prev, ...next };
    },
    {
      severity: vulnerabilitySeverityOptions[0],
      publishedAt: publishedAtOptions[0],
      isPatchable: patchAvailableOptions[0],
      vulnerabilityType: VulnerabilityType.Open,
      hasPublicExploit: exploitableOptions[0],
      currentRecord: {},
      searchTerm: '',
      finalSearchTerm: '',
    },
  );
  const updateValue = useCallback((field, value) => {
    updateState({ [field]: value });
  }, []);

  useEffect(() => {
    if (isEmpty(insight)) return;

    const published = getPublishedTime(state.publishedAt?.value);

    const payload = {
      nativeResource: insight?.native_name,
      sortBy:
        sortInfo.sortField === 'cvss_v3.score'
          ? 'cvss_score'
          : sortInfo.sortField,
      sortOrder: sortInfo.sortOrder,
      page: resourceVulnerabilities.page.info.page_number,
      pageSize: resourceVulnerabilities.page.info.page_size,
      severity: state.severity.value,
      isPatchable:
        state.isPatchable.value === 'yes'
          ? true
          : state.isPatchable.value === 'no'
          ? false
          : undefined,
      ...(state.vulnerabilityType === VulnerabilityType.Resolved
        ? { resolvedAt: published }
        : { effectiveStart: published }),
      resolved: state.vulnerabilityType === VulnerabilityType.Resolved,
    };

    if (insight?.agnostic_name === Resources.Image)
      dispatch(
        actions.getAMIVulnerabilities({
          q: {
            resourceId: insight?.uuid,
            ...payload,
          },
        }),
      );
    else
      dispatch(
        actions.getResourceVulnerabilities({
          q: {
            resourceUuid: insight?.uuid,
            instanceId: isContainer
              ? undefined
              : insight?.native_name ===
                NativeResources.ElasticContainerRegistry
              ? insight?.image_digest
              : insight?.resource_id,
            containerId: isContainer ? insight?.resource_id : undefined,
            ...payload,
            useScanVaultVulnerabilityFindings,
          },
        }),
      );
  }, [
    resourceVulnerabilities.page.info,
    dispatch,
    insight,
    sortInfo,
    state.severity,
    state.isPatchable,
    state.publishedAt,
    state.vulnerabilityType,
    isContainer,
    useScanVaultVulnerabilityFindings,
  ]);

  const columns = [
    {
      header: <Box pl="thLeftPaddingWithIcon">CVE</Box>,
      accessor: 'cve_id',
      align: 'left',
      render: ({ row }) => (
        <WithResourceIcon
          resourceType={IconTypes.Vulnerabilities}
          color={customTheme.colors?.[row.severity?.toLowerCase()]}
          useCustomColor
        >
          <CustomTooltip label={row?.cve_id ?? row?.name}>
            {row?.cve_id ?? row?.name}
          </CustomTooltip>
        </WithResourceIcon>
      ),
    },
    {
      header: 'Package',
      align: 'left',
      render: ({ row }) => (
        <StackedCell
          upper={row?.package_name ?? row?.package_names?.join(', ')}
          lower={row?.installed_version ?? row?.package_version}
        />
      ),
    },
    {
      header: 'Patch Version',
      accessor: 'fixed_in_version',
      align: 'left',
    },
    {
      header: 'Severity',
      accessor: 'severity',
      align: 'left',
      sortKey: 'severity',
      render: ({ value }) => renderSeverityBar({ value, isInline: false }),
    },
    {
      header: 'CVSS Score',
      accessor: 'cvss_v3.score',
      sortKey: 'score',
      align: 'center',
      render: ({ row }) => {
        const value =
          row?.cvss_v3?.score ?? row?.cvss_v4?.score ?? row?.CVSS2_SCORE;
        const label = !!row?.cvss_v3?.score
          ? 'CVSS V3: ' + value
          : !!row?.cvss_v4?.score
          ? 'CVSS V4: ' + value
          : 'CVSS: ' + value;

        return (
          <CustomTooltip label={label}>
            <Center pr="50%">
              {value || value === 0
                ? renderCVSSScoreTag(round(value, 1), row?.severity)
                : '-'}
            </Center>
          </CustomTooltip>
        );
      },
    },
    {
      header: 'Has Public Exploit',
      render: ({ row }) => (row?.exploitable ? 'Yes' : 'No'),
    },
    ...(insight?.agnostic_name === Resources.ServerlessCompute ||
    insight?.agnostic_name === Resources.Image
      ? [
          {
            header: 'Source',
            align: 'left',
            render: () => '-',
            styles: {
              width: '150px',
              minW: '150px',
              pr: 0,
              cell: { maxWidth: '60px', minW: '60px', pr: 0 },
            },
          },
        ]
      : [
          {
            header: 'Detection',
            align: 'left',
            render: () => 'Base Package',
          },
        ]),
    {
      header: 'Actions',
      render: ({ row }) => (
        <Center>
          <ActionButton
            label={'Show all entities'}
            icon={<ListIcon />}
            onClick={e => {
              e.stopPropagation();
              hostsDisclosure.onOpen();
              updateState({ currentRecord: row });
            }}
          />
        </Center>
      ),
      styles: { width: '60px', cell: { maxWidth: '60px' } },
    },
  ];

  /*...//containerImageId ||
    (insight?.native_name === NativeResources.ElasticContainerRegistry
      ? [
          {
            header: 'CVSS Vector',
            accessor: 'cvss_v3',
            align: 'left',
            render: ({ value }) => renderCVSSScore(value?.vector),
          },
        ]
      : []),*/

  return (
    <BorderedBox flex={1} h="full" minH="400px">
      <Stack w="full" h="full" spacing={5}>
        <ResourceVulnerabilitiesFilters
          publishedAt={state.publishedAt}
          severity={state.severity}
          vulnerabilityType={state.vulnerabilityType}
          isPatchable={state.isPatchable}
          updateValue={updateValue}
          hasPublicExploit={state.hasPublicExploit}
          searchTerm={state.searchTerm}
        />
        <Box flex={1} w="full" h="full">
          <Table
            data={resourceVulnerabilities.data}
            columns={columns}
            isLoading={resourceVulnerabilities.isLoading}
            pagination={{
              pageInfo: resourceVulnerabilities.page.info,
              totalCount: resourceVulnerabilities.page.totalCount,
              onChange(info: PageInfo) {
                dispatch(actions.updateResourceVulnerabilitiesPagination(info));
              },
            }}
            onRowClick={row => {
              updateValue('currentRecord', row);
              cveDetailsDisclosure.onOpen();
            }}
            sortBy={sortInfo}
            onSort={s => {
              if (!isEqual(sortInfo, s)) {
                setSortInfo(s);
              }
            }}
          />
          {hostsDisclosure.isOpen && (
            <HostsDrawer
              row={state.currentRecord}
              isOpen={hostsDisclosure.isOpen}
              onClose={hostsDisclosure.onClose}
              selectedTab="cve"
            />
          )}
          {cveDetailsDisclosure.isOpen && (
            <CVEDetails
              isOpen={cveDetailsDisclosure.isOpen}
              onClose={cveDetailsDisclosure.onClose}
              currentTechInstance={state.currentRecord} //styles={{ content: { maxW: '1100px' } }}
            />
          )}
        </Box>
      </Stack>
    </BorderedBox>
  );
};
